// src/lib/sw-update/chunk-recovery.ts

/**
 * Recovery from lazy-chunk fetch failures after a deploy.
 *
 * Route chunks are content-hashed and a deploy replaces `dist/` wholesale, so
 * the previous build's chunks 404 (`location /assets/` ends in
 * `try_files $uri =404` — see `nginx.conf`). TanStack Router code-splits per
 * route (`autoCodeSplitting` in `vite.config.ts`), so a tab opened before the
 * deploy fails on essentially every route it has not already visited.
 *
 * The fix is to get onto the current build, which is `takeNewBuild`'s job — a
 * 404'd chunk is simply a third trigger for it, alongside a route change and
 * the banner. All this module adds is recognising the failure and rate-limiting
 * the response.
 */

import {takeNewBuild} from './take-new-build';

/** sessionStorage key holding the timestamp of this tab's last recovery. */
const RECOVERY_KEY = 'dyna:chunk-recovery-at';

/**
 * Minimum gap between two recoveries in one tab. A deploy needs exactly one,
 * so this only ever bites the pathological case — a build whose chunks are
 * genuinely missing — where it caps the damage at one reload a minute instead
 * of a hot loop. It doubles as the same-tick guard: the stamp is written
 * before the handoff starts, so a second failure in the same tick is refused.
 */
const RECOVERY_COOLDOWN_MS = 60 * 1000;

/**
 * Browsers word this failure differently and none of them expose a
 * machine-readable code, so matching the message is the only option:
 * Chrome/Vite say "Failed to fetch dynamically imported module", Vite's own
 * preload helper says "error loading dynamically imported module" (and
 * "Unable to preload CSS" for a missing stylesheet), Safari says "Importing a
 * module script failed". `ChunkLoadError` is the bundler-era name kept in case
 * a dependency still throws it.
 */
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS|Importing a module script failed|ChunkLoadError/i;

/** True when `error` looks like a chunk that is no longer on the server. */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return CHUNK_ERROR_PATTERN.test(error.message) || CHUNK_ERROR_PATTERN.test(error.name);
  }
  if (typeof error === 'string') {
    return CHUNK_ERROR_PATTERN.test(error);
  }
  return false;
}

/** True while this tab's last recovery is still inside the cooldown. */
function withinCooldown(): boolean {
  try {
    const raw = globalThis.sessionStorage.getItem(RECOVERY_KEY);
    if (raw === null) return false;
    const previous = Number(raw);
    if (!Number.isFinite(previous)) return false;
    return Date.now() - previous < RECOVERY_COOLDOWN_MS;
  } catch {
    // Storage unavailable (sandboxed frame, blocked site data). We lose the
    // cooldown but not correctness: a recovery still reaches the new build.
    return false;
  }
}

function markRecovered(): void {
  try {
    globalThis.sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
  } catch {
    // See withinCooldown — nothing actionable.
  }
}

/**
 * Respond to one chunk failure, however it surfaced: rate-limit it, decide
 * whether to swallow it, and get the tab onto the current build.
 *
 * The event is swallowed only when recovery is actually coming, because that is
 * the only case the reasoning covers: the page is about to be replaced, so
 * letting the error through adds console noise the user cannot act on. Inside
 * the cooldown nothing is coming, and then the 404 is the single most useful
 * thing on the page — it is what distinguishes a build whose chunks are
 * genuinely missing from one that is merely stale. Suppressing it there would
 * leave a broken deploy looking like silence.
 */
function onChunkFailure(event: Event, error: unknown): void {
  if (!isChunkLoadError(error)) return;
  if (withinCooldown()) return;

  event.preventDefault();
  // Stamped before acting, not after: the no-worker path reloads before it
  // returns, so there is no "after" to stamp in.
  markRecovered();
  void takeNewBuild();
}

/**
 * Listen for chunk failures anywhere in the app. Dynamic imports surface as
 * unhandled rejections; `error` catches the script-tag path too. Called once
 * from `main.tsx`.
 *
 * Returns a teardown function.
 */
export function installChunkRecovery(): () => void {
  const onRejection = (event: PromiseRejectionEvent) => onChunkFailure(event, event.reason);
  const onError = (event: ErrorEvent) => onChunkFailure(event, event.error ?? event.message);

  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('error', onError);

  return () => {
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onError);
  };
}
